// =============================================
// SERVICE: API do Conferente PWA
// =============================================

import { apiService } from './api';
import { buildApiUrl } from './config';
import type { Conferencia, ConferenciaItem, ConferenciaLeitura, ResumoLeituras, ProdutividadeResponse } from '../types/conferencia';
// Importa e re-exporta funções de autenticação do conferente (definidas em conferenteAuth.ts para evitar ciclo com api.ts)
import { getConferenteToken, isConferenteTokenExpired, handleConferenteSessionExpired } from './conferenteAuth';
export { getConferenteToken, isConferenteTokenExpired, handleConferenteSessionExpired } from './conferenteAuth';

const CONFERENTE_TOKEN_KEY = 'conferente_token';
const CONFERENTE_USER_KEY = 'conferente_user';
const CONFERENTE_SESSAO_KEY = 'conferente_sessao_id';
const CONFERENTE_CNPJ_SLUG_KEY = 'conferente_cnpj_slug';

export function setConferenteCnpjSlug(cnpj: string) {
  localStorage.setItem(CONFERENTE_CNPJ_SLUG_KEY, cnpj);
}

export function clearConferenteCnpjSlug() {
  localStorage.removeItem(CONFERENTE_CNPJ_SLUG_KEY);
}

export function getConferenteCnpjSlug(): string | null {
  return localStorage.getItem(CONFERENTE_CNPJ_SLUG_KEY);
}

export function getConferenteLoginUrl(): string {
  const cnpj = getConferenteCnpjSlug();
  return cnpj ? `/conferente/${cnpj}` : '/conferente/login';
}

// =============================================
// INTERFACES PARA NOVA ENTIDADE CONFERENTE
// =============================================

export interface ConferenteNovo {
  id: number;
  cpf: string;
  nome: string;
  pin_temporario: boolean;
  telefone?: string;
  tipo_conferente: string;
  valor_diaria?: number;
  valor_hora?: number;
  dias_semana?: number;
  valor_mensal?: number;
  endereco?: Record<string, unknown>;
  empresa_id: number;
  filial_id?: number;
  status: 'pendente' | 'aprovado' | 'reprovado';
  ativo: boolean;
  aprovado_por?: number;
  aprovado_em?: string;
  criado_em: string;
  atualizado_em: string;
  empresa_nome?: string;
  filial_nome?: string;
}

export interface EmpresaInfoPublica {
  nome: string;
  cnpj: string;
  logomarca_url?: string;
}

// =============================================
// AUTO-CADASTRO E LOGIN POR CPF+PIN (NOVA ENTIDADE)
// =============================================

export async function buscarEmpresaPorCnpj(cnpj: string): Promise<{ success: boolean; empresa: EmpresaInfoPublica }> {
  const response = await fetch(buildApiUrl(`/conferentes/empresa/${cnpj.replace(/\D/g, '')}/info`));
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Empresa não encontrada');
  return data;
}

export async function cadastrarConferente(
  cnpj: string,
  dados: { cpf: string; nome: string; pin: string; dispositivo_info?: Record<string, unknown> }
): Promise<{
  success: boolean;
  token: string;
  sessao_id: number;
  conferente: ConferenteNovo;
  message: string;
}> {
  const response = await fetch(buildApiUrl(`/conferentes/cadastro/${cnpj.replace(/\D/g, '')}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(dados)
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Erro no cadastro');

  // Salvar dados no localStorage para auto-login
  localStorage.setItem(CONFERENTE_TOKEN_KEY, data.token);
  localStorage.setItem(CONFERENTE_USER_KEY, JSON.stringify({
    ...data.conferente,
    tipo: 'conferente_novo',
    is_conferente: true
  }));
  localStorage.setItem(CONFERENTE_SESSAO_KEY, String(data.sessao_id));

  return data;
}

export async function loginConferenteCpf(
  cnpj: string,
  cpf: string,
  pin: string,
  dispositivo_info?: Record<string, unknown>
): Promise<{
  success: boolean;
  token: string;
  sessao_id: number;
  pin_temporario: boolean;
  conferente: ConferenteNovo;
}> {
  const response = await fetch(buildApiUrl(`/conferentes/login/${cnpj.replace(/\D/g, '')}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ cpf: cpf.replace(/\D/g, ''), pin, dispositivo_info })
  });

  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Erro no login');

  // Salvar dados no localStorage
  localStorage.setItem(CONFERENTE_TOKEN_KEY, data.token);
  localStorage.setItem(CONFERENTE_USER_KEY, JSON.stringify({
    ...data.conferente,
    tipo: 'conferente_novo',
    is_conferente: true
  }));
  localStorage.setItem(CONFERENTE_SESSAO_KEY, String(data.sessao_id));

  return data;
}

export async function alterarPinConferente(pinAtual: string, pinNovo: string) {
  const token = getConferenteToken();
  if (!token || isConferenteTokenExpired()) {
    handleConferenteSessionExpired();
    return new Promise(() => {}); // Nunca resolve - redirect em andamento
  }

  const response = await fetch(buildApiUrl('/conferentes/alterar-pin'), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({ pin_atual: pinAtual, pin_novo: pinNovo })
  });
  if (response.status === 401) {
    handleConferenteSessionExpired();
    return new Promise(() => {});
  }
  const data = await response.json();
  if (!response.ok) throw new Error(data.message || 'Erro ao alterar PIN');
  return data;
}

// =============================================
// CRUD DE CONFERENTES (para gestores)
// =============================================

export async function listarConferentes(params?: {
  page?: number; limit?: number; search?: string; status?: string;
  tipo_conferente?: string; filial_id?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
  }
  const qs = searchParams.toString();
  return apiService.request<{
    success: boolean;
    conferentes: ConferenteNovo[];
    pagination: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
  }>(buildApiUrl('/conferentes') + (qs ? `?${qs}` : ''));
}

export async function buscarConferenteById(id: number) {
  return apiService.request<{
    success: boolean;
    conferente: ConferenteNovo;
    clientes: { id: number; razao_social: string; cnpj: string }[];
  }>(buildApiUrl(`/conferentes/${id}`));
}

export async function criarConferenteManual(data: Partial<ConferenteNovo> & { pin: string; cliente_ids?: number[] }) {
  return apiService.request<{ success: boolean; conferente: ConferenteNovo }>(
    buildApiUrl('/conferentes'),
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function atualizarConferente(id: number, data: Partial<ConferenteNovo> & { cliente_ids?: number[] }) {
  return apiService.request<{ success: boolean; conferente: ConferenteNovo }>(
    buildApiUrl(`/conferentes/${id}`),
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function aprovarConferente(id: number, dados?: { cliente_ids?: number[]; filial_id?: number }) {
  return apiService.request<{ success: boolean; conferente: ConferenteNovo }>(
    buildApiUrl(`/conferentes/${id}/aprovar`),
    { method: 'POST', body: JSON.stringify(dados || {}) }
  );
}

export async function reprovarConferente(id: number) {
  return apiService.request<{ success: boolean; conferente: ConferenteNovo }>(
    buildApiUrl(`/conferentes/${id}/reprovar`),
    { method: 'POST' }
  );
}

export async function resetarPinConferente(id: number) {
  return apiService.request<{ success: boolean; message: string }>(
    buildApiUrl(`/conferentes/${id}/reset-pin`),
    { method: 'POST' }
  );
}

export async function excluirConferente(id: number) {
  return apiService.request<{ success: boolean; message: string }>(
    buildApiUrl(`/conferentes/${id}`),
    { method: 'DELETE' }
  );
}

// =============================================
// AUTENTICAÇÃO
// =============================================

export async function loginConferente(email: string, senha: string, dispositivo_info?: Record<string, unknown>) {
  const response = await fetch(buildApiUrl('/conferente/login'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, senha, dispositivo_info })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.message || 'Erro no login');
  }

  // Salvar dados do conferente no localStorage
  localStorage.setItem(CONFERENTE_TOKEN_KEY, data.token);
  localStorage.setItem(CONFERENTE_USER_KEY, JSON.stringify(data.usuario));
  localStorage.setItem(CONFERENTE_SESSAO_KEY, String(data.sessao_id));

  return data;
}

export async function logoutConferente() {
  const token = getConferenteToken();
  const sessaoId = localStorage.getItem(CONFERENTE_SESSAO_KEY);

  if (token) {
    try {
      await fetch(buildApiUrl('/conferente/logout'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ sessao_id: sessaoId ? parseInt(sessaoId) : undefined })
      });
    } catch {
      // Continuar mesmo com erro
    }
  }

  localStorage.removeItem(CONFERENTE_TOKEN_KEY);
  localStorage.removeItem(CONFERENTE_USER_KEY);
  localStorage.removeItem(CONFERENTE_SESSAO_KEY);
  // Cache offline NAO e limpo no logout - dados mestres permanecem
  // para uso offline. So conferencias finalizadas e sincronizadas sao removidas.
}

export function getConferenteUser() {
  const data = localStorage.getItem(CONFERENTE_USER_KEY);
  if (!data) return null;
  try {
    return JSON.parse(data);
  } catch {
    return null;
  }
}

export function isConferenteLoggedIn(): boolean {
  if (!getConferenteToken() || !getConferenteUser()) return false;
  // Também verifica se o token não está expirado
  return !isConferenteTokenExpired();
}

// Helper para requests autenticados do conferente
function conferenteRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = getConferenteToken();
  if (!token || isConferenteTokenExpired()) {
    handleConferenteSessionExpired();
    return new Promise(() => {}); // Nunca resolve - redirect em andamento
  }

  return fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  }).then(async res => {
    const data = await res.json();
    if (res.status === 401) {
      // Token expirado ou inválido - redirecionar silenciosamente
      handleConferenteSessionExpired();
      return new Promise(() => {}); // Nunca resolve - redirect em andamento
    }
    if (!res.ok) throw new Error(data.message || 'Erro na requisição');
    return data;
  });
}

// =============================================
// CLIENTES VINCULADOS AO CONFERENTE
// =============================================

export interface ClienteConferente {
  id: number;
  razao_social: string;
  cnpj: string;
}

export async function buscarClientesConferente(): Promise<{
  success: boolean;
  clientes: ClienteConferente[];
  auto_select: boolean;
  sem_vinculos?: boolean;
}> {
  return conferenteRequest<{
    success: boolean;
    clientes: ClienteConferente[];
    auto_select: boolean;
    sem_vinculos?: boolean;
  }>(buildApiUrl('/conferente/clientes'));
}

// =============================================
// GERAR NOVO PALETE
// =============================================

export interface GerarPaleteResponse {
  success: boolean;
  precisa_selecionar_cliente: boolean;
  precisa_selecionar_area?: boolean;
  precisa_selecionar_tipo_palete?: boolean;
  clientes?: ClienteConferente[];
  areas?: AreaDestino[];
  area_entrega_id?: number;
  palete?: {
    id: number;
    numero_palete: number;
    doca: string | null;
    area_entrega_id: number;
    area_nome: string;
    area_codigo: string;
    area_regiao: string;
    operacao_id: number;
    numero_operacao: number;
    tipo_palete?: string;
    data_agendamento?: string;
  };
  cliente?: {
    id: number;
    razao_social: string;
    cnpj: string;
  };
  etiqueta_configs?: any[];
  etiqueta_modelos?: any[];
  leituras?: any[];
  total_volumes?: number;
}

export async function gerarNovoPaleteConferente(params?: {
  clienteId?: number;
  areaEntregaId?: number;
  tipoPalete?: string;
  dataAgendamento?: string;
}): Promise<GerarPaleteResponse> {
  const body: Record<string, unknown> = {};
  if (params?.clienteId) body.cliente_id = params.clienteId;
  if (params?.areaEntregaId) body.area_entrega_id = params.areaEntregaId;
  if (params?.tipoPalete) body.tipo_palete = params.tipoPalete;
  if (params?.dataAgendamento) body.data_agendamento = params.dataAgendamento;
  return conferenteRequest<GerarPaleteResponse>(
    buildApiUrl('/conferente/gerar-palete'),
    { method: 'POST', body: JSON.stringify(body) }
  );
}

// =============================================
// ÁREAS DE DESTINO (filtradas por filial do conferente)
// =============================================

export interface AreaDestino {
  id: number;
  nome: string;
  codigo: string | null;
  regiao: string | null;
  filial_id: number | null;
}

export async function listarAreasDestinoConferente(): Promise<{
  success: boolean;
  areas: AreaDestino[];
}> {
  return conferenteRequest<{
    success: boolean;
    areas: AreaDestino[];
  }>(buildApiUrl('/conferente/areas-destino'));
}

// =============================================
// CONFERÊNCIAS DO CONFERENTE
// =============================================

export async function getConferenteMe() {
  return conferenteRequest<{
    success: boolean;
    usuario: { id: number; nome: string; email: string; tipo: string; empresa_id: number; foto_perfil_url?: string; empresa_nome: string };
    conferencias_ativas: number;
  }>(buildApiUrl('/conferente/me'));
}

export async function listarConferenciasConferente(params?: {
  status?: string;
  tipo?: string;
  page?: number;
  limit?: number;
}) {
  const searchParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        searchParams.append(key, String(value));
      }
    });
  }
  const qs = searchParams.toString();
  const url = buildApiUrl('/conferente/conferencias') + (qs ? `?${qs}` : '');

  return conferenteRequest<{
    success: boolean;
    conferencias: Conferencia[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }>(url);
}

// =============================================
// CONFERÊNCIA (reutiliza endpoints existentes com token do conferente)
// =============================================

export async function buscarConferenciaConferente(id: number) {
  return conferenteRequest<{
    success: boolean;
    conferencia: Conferencia;
    itens: ConferenciaItem[];
    resumo_leituras: ResumoLeituras;
  }>(buildApiUrl(`/conferencias/${id}`));
}

export async function criarConferenciaConferente(data: {
  tipo: string;
  cliente_id?: number;
  placa_veiculo?: string;
  transportadora?: string;
  manifesto?: string;
  rota?: string;
  observacoes?: string;
}) {
  const user = getConferenteUser();
  const bodyData = { ...data, conferente_id: user?.id };
  return conferenteRequest<{ success: boolean; conferencia: Conferencia }>(
    buildApiUrl('/conferencias'),
    { method: 'POST', body: JSON.stringify(bodyData) }
  );
}

export async function registrarLeituraConferente(conferenciaId: number, data: {
  tipo_leitura: string;
  codigo_lido: string;
  quantidade?: number;
  dados_decodificados?: Record<string, unknown>;
  lido_em?: string;
}) {
  return conferenteRequest<{
    success: boolean;
    duplicado: boolean;
    encontrado: boolean;
    leitura: ConferenciaLeitura;
    item?: ConferenciaItem;
  }>(
    buildApiUrl(`/conferencias/${conferenciaId}/leituras`),
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function finalizarConferenciaConferente(id: number) {
  return conferenteRequest<{ success: boolean; conferencia: Conferencia }>(
    buildApiUrl(`/conferencias/${id}`),
    { method: 'PUT', body: JSON.stringify({ status: 'concluida', fim_conferencia: new Date().toISOString() }) }
  );
}

export async function listarLeiturasConferente(conferenciaId: number, limit = 30) {
  return conferenteRequest<{
    success: boolean;
    leituras: ConferenciaLeitura[];
  }>(buildApiUrl(`/conferencias/${conferenciaId}/leituras?limit=${limit}`));
}

// =============================================
// PALETES EM ANDAMENTO (para dashboard do conferente)
// =============================================

export interface PaleteEmAndamento {
  id: number;
  numero_palete: number;
  doca: string | null;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  operacao_id: number;
  numero_operacao: number;
  cliente_id: number | null;
  cliente_razao_social: string | null;
  tipo_palete: string;
  data_agendamento?: string;
  total_volumes: number;
  total_esperado: number;
  volumes_faltantes: number;
  total_duplicados: number;
  tem_divergencia: boolean;
  disponivel: boolean;
}

export async function listarPaletesConferente(clienteId?: number) {
  const params: Record<string, unknown> = {};
  if (clienteId) params.cliente_id = clienteId;
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) searchParams.append(key, String(value));
  });
  const qs = searchParams.toString();
  const url = buildApiUrl('/conferencias/carregamento/listar-paletes') + (qs ? `?${qs}` : '');
  return conferenteRequest<{
    success: boolean;
    paletes: PaleteEmAndamento[];
    resumo: { total: number; disponiveis: number; divergentes: number };
  }>(url);
}

// =============================================
// PALETES HISTÓRICO (para página de histórico do conferente)
// =============================================

export interface PaleteHistorico {
  id: number;
  numero_palete: number;
  doca: string | null;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  operacao_id: number;
  numero_operacao: number;
  tipo_palete: string;
  data_agendamento?: string;
  operacao_status: string;
  status: string;
  total_volumes: number;
  total_duplicados: number;
  cliente_razao_social?: string;
}

export async function listarPaletesHistoricoConferente() {
  const url = buildApiUrl('/conferente/paletes-historico');
  return conferenteRequest<{
    success: boolean;
    paletes: PaleteHistorico[];
  }>(url);
}

// =============================================
// PRODUTIVIDADE (usado pelo painel admin)
// =============================================

function buildUrlWithParams(path: string, params?: Record<string, unknown>): string {
  const url = buildApiUrl(path);
  if (!params) return url;
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `${url}?${qs}` : url;
}

export async function getProdutividade(params?: {
  periodo?: string;
  tipo?: string;
  dataInicio?: string;
  dataFim?: string;
}): Promise<ProdutividadeResponse> {
  return apiService.request<ProdutividadeResponse>(
    buildUrlWithParams('/conferencias/produtividade', params as Record<string, unknown>)
  );
}

export async function getProdutividadeConferente(usuarioId: number, periodo?: string) {
  return apiService.request<{
    success: boolean;
    conferente: { id: number; nome: string; email: string; foto_perfil_url?: string };
    estatisticas: {
      total_conferencias: number;
      concluidas: number;
      divergentes: number;
      pre_carregamentos: number;
      carregamentos: number;
      descarregamentos: number;
      total_itens: number;
      total_divergencias: number;
      tempo_medio_minutos: number;
    };
    leituras: { total: number; ok: number; duplicados: number; nao_encontrados: number };
    leituras_por_dia: { data: string; total: number }[];
    ultimas_conferencias: Conferencia[];
    sessoes: { login_em: string; logout_em?: string; dispositivo_info?: Record<string, unknown> }[];
    periodo: string;
  }>(buildUrlWithParams(`/conferencias/produtividade/${usuarioId}`, { periodo }));
}
